import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import TableCrudEnhanced from '../../components/TableCrudEnhanced';
import Api, { exceptionNotificationAPI } from '../../services/api';

const INITIAL_FILTERS = { isActive: true };

const CompanyIndex = () => {
    const navigate = useNavigate();
    const [cultureOptions, setCultureOptions] = useState([
        { value: '', label: 'All' }
    ]);

    useEffect(() => {
        Api.get('Culture/GetAllCultures')
            .then((result) => {
                const options = result.data.response
                    .map(culture => ({
                        value: culture.cultureID,
                        label: culture.cultureDisplayName
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                setCultureOptions([{ value: '', label: 'All' }, ...options]);
            })
            .catch(exceptionNotificationAPI);
    }, []);

    const filterConfig = useMemo(() => [
        {
            type: "text",
            name: "name",
            label: "Name",
            placeholder: "Search by name..."
        },
        {
            type: "text",
            name: "currencyTypeDescription",
            label: "Currency Type",
            placeholder: "Search by currency type..."
        },
        {
            type: "select",
            name: "cultureID",
            label: "Report",
            options: cultureOptions
        },
        {
            type: "select",
            name: "isActive",
            label: "Active",
            options: [
                { value: '', label: 'All' },
                { value: true, label: 'Yes' },
                { value: false, label: 'No' }
            ]
        }
    ], [cultureOptions]);

    const columns = useMemo(() => [
        { 
            title: 'Name', 
            dataIndex: 'name', 
            key: 'name', 
            sortable: true 
        },
        { 
            title: 'Currency Type', 
            dataIndex: 'currencyType.description', 
            key: 'currencyType.description', 
            sortable: true 
        },
        {
            title: 'Report',
            dataIndex: 'companyCulture',
            key: 'companyCulture',
            sortable: true,
            render: (companyCultures) => companyCultures
                ?.map(companyCulture => companyCulture.culture?.description)
                .filter(Boolean)
                .join(', ') || '-'
        },
        { 
            title: 'Active', 
            dataIndex: 'isActive', 
            key: 'isActive', 
            width: 80,
            render: (value, record, index, handleToggleActive) => (
                <span 
                    className={`badge ${value ? 'bg-success' : 'bg-secondary'}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleToggleActive && handleToggleActive(record)}
                    title={`Click to ${value ? 'deactivate' : 'activate'}`}
                >
                    {value ? 'Active' : 'Inactive'}
                </span>
            )
        },
        { 
            title: 'Show in Dashboard', 
            dataIndex: 'showInDashboard', 
            key: 'showInDashboard', 
            width: 120,
            render: (value) => (
                <span className={`badge ${value ? 'bg-success' : 'bg-secondary'}`}>
                    {value ? 'Yes' : 'No'}
                </span>
            )
        },
        { 
            title: 'Adjust ESG Year', 
            dataIndex: 'isESGYear', 
            key: 'isESGYear', 
            width: 120,
            render: (value) => (
                <span className={`badge ${value ? 'bg-success' : 'bg-secondary'}`}>
                    {value ? 'Yes' : 'No'}
                </span>
            )
        },
        { 
            title: 'Is FSM Supplier', 
            dataIndex: 'isFSMSupplier', 
            key: 'isFSMSupplier', 
            width: 120,
            render: (value) => (
                <span className={`badge ${value ? 'bg-success' : 'bg-secondary'}`}>
                    {value ? 'Yes' : 'No'}
                </span>
            )
        },
        { 
            title: 'Deleted', 
            dataIndex: 'isDeleted', 
            key: 'isDeleted', 
            width: 100,
            render: (value, record, index, handleToggleActive, handleToggleDeleted) => (
                <span 
                    className={`badge ${!value ? 'bg-success' : 'bg-secondary'}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleToggleDeleted && handleToggleDeleted(record)}
                    title={`Click to ${!value ? 'delete' : 'restore'}`}
                >
                    {!value ? 'Not Deleted' : 'Deleted'}
                </span>
            )
        }
    ], []);

    const actions = useMemo(() => [
        {
            label: 'Edit',
            onClick: (record) => navigate(`/company/edit/${record.companyID}`),
            type: 'primary'
        }
    ], [navigate]);

    return (
        <TableCrudEnhanced
            title="Supplier"
            apiEndpoint="/Company/getAllPaginated"
            toggleActiveEndpoint="/Company/toggleActive"
            toggleDeletedEndpoint="/Company/toggleDeleted"
            newRoute="/company/new"
            columns={columns}
            filterConfig={filterConfig}
            actions={actions}
            rowKey="companyID"
            initialPageSize={10}
            initialFilters={INITIAL_FILTERS}
            enableFilters={true}
            showAddButton={true}
        />
    );
};

export default CompanyIndex;
